import { createSelector } from "@reduxjs/toolkit";

import { StoreState } from "~/redux/reducers";

import { workstationApi } from "~/redux/warehouse/workstation";

import {
  selectPortStatusByPortKeyMap,
  selectHandRaisedByIdMap,
  selectPortBinsByPortKeyMap,
  selectGridModeByIdMap
} from "./andon.slice";
import type { AndonStatus, PongData, AndonEventWithMeta } from "./andon.types";
import { getAndonPortEntry } from "./andon.utils";

const EMPTY_ARRAY: number[] = [];

export const selectWorkstationStatus = (workstationId: string) =>
  createSelector(
    [
      (state: StoreState) => state.andon.portStatusByPortKey,
      (state: StoreState) => state
    ],
    (portStatusByPortKey, state): AndonStatus => {
      const workstation = workstationApi.endpoints.getWorkstations
        .select()(state)
        .data?.find((ws) => ws.id === workstationId);

      if (!workstation) return "good";

      const portIds = [
        ...workstation.ports.map((p) => p.portId.toString()),
        ...(workstation.ports
          .map((p) => p.parentPortId?.toString())
          .filter(Boolean) as string[])
      ];

      return portIds.some(
        (pid) =>
          getAndonPortEntry(
            portStatusByPortKey,
            pid,
            workstation.autostoreGridId
          ) === "bad"
      )
        ? "bad"
        : "good";
    }
  );

export const selectPortStatus =
  (portId: string, gridId?: string) =>
  (state: StoreState): AndonStatus =>
    getAndonPortEntry(selectPortStatusByPortKeyMap(state), portId, gridId) ??
    "good";

export const selectPortHasBins =
  (portId: string, gridId?: string) =>
  (state: StoreState): number[] =>
    getAndonPortEntry(selectPortBinsByPortKeyMap(state), portId, gridId) ??
    EMPTY_ARRAY;

export const selectGridSystemMode = (gridId: string) =>
  createSelector(
    (state: StoreState) => selectGridModeByIdMap(state),
    (modes) => modes[gridId] ?? null
  );

export const selectPongLoopHistory =
  (workstationId: string) =>
  (
    state: StoreState
  ): { pongData: PongData; loopTime: number; timestamp: string }[] =>
    state.andon.workstationPongHistory[workstationId] ?? [];

export const selectEventHistory = (state: StoreState) =>
  state.andon.eventHistory;

export const selectEventHistoryByWorkstationIdMap = (ids: string[]) =>
  createSelector(selectEventHistory, (events) => {
    const grouped: Record<string, AndonEventWithMeta[]> = {};

    for (const id of ids) {
      grouped[id] = [];
    }

    for (const evt of events) {
      if (evt.workstationId && grouped[evt.workstationId]) {
        grouped[evt.workstationId].push(evt);
      }
    }

    return grouped;
  });

export const selectLooseThreadEvents = createSelector(
  (state: StoreState) => state.andon.eventHistory,
  (events): AndonEventWithMeta[] => events.filter((evt) => !evt.workstationId)
);

export const selectLayoutCode = createSelector(
  (state: StoreState) => state.andon.boardLayout,
  (state: StoreState) => state.andon.boardDimensions,
  (layout, dimensions) =>
    JSON.stringify({
      layout,
      dimensions
    })
);

export const selectHandRaiseStartTime = (id: string) => (state: StoreState) =>
  state.andon.handRaisedByWorkstationId[id]?.timestamp ?? null;

export const selectHandRaisedForWorkstation = (workstationId?: string) =>
  createSelector(selectHandRaisedByIdMap, (map) =>
    workstationId && map[workstationId]
      ? map[workstationId]
      : { raised: false, timestamp: null, requestType: null }
  );
